/**
 * Category picker — a searchable bottom-sheet dropdown.
 *
 * We ship with 40+ categories today; the flat pill grid on
 * `/play/solo` and `/play/duel` had grown into a scroll-wall. This
 * component replaces it with:
 *
 *   1. A compact trigger button showing the current selection.
 *   2. A slide-up sheet with a search input + scrollable list.
 *
 * Vanilla-JS, no dependencies. Works on both pages via:
 *
 *   const html = renderCategoryTrigger({ id: 'cat-picker' });
 *   ...page.innerHTML = `... ${html} ...`;
 *   attachCategoryPicker('cat-picker', { categories, selected, onChange });
 */

import { escape } from './form.js';

// One open sheet at a time — tracked at module scope so a stray second
// tap doesn't double-mount.
let _openSheet = null;

/** HTML for the trigger button. Insert into your page markup.
 *
 * `mark` is the emoji glyph shown on the left of the trigger — defaults
 * to a target emoji for category pickers. Pass a different glyph
 * (e.g. `👤` for a friend picker) when the trigger is picking
 * something other than a topic.
 *
 * `emphasis: true` adds a teal accent border + hint sublabel so the
 * trigger reads as a primary decision on pages where the flat pill
 * grid of siblings visually outweighs it (e.g. the duel-create page,
 * where the topic picker sits between a friend picker and a row of
 * stake pills). */
export function renderCategoryTrigger(
  id,
  { placeholder = 'Choose category', mark = '🎯', emphasis = false, hint = 'Tap to pick' } = {},
) {
  const cls = `cat-picker-trigger${emphasis ? ' cat-picker-trigger--emphasis' : ''}`;
  const hintHtml = emphasis
    ? `<span class="cat-picker-hint" aria-hidden="true">${escape(hint)}</span>`
    : '';
  return `
    <button type="button"
            class="${cls}"
            id="${escape(id)}"
            data-placeholder="${escape(placeholder)}"
            aria-haspopup="dialog"
            aria-expanded="false">
      <span class="cat-picker-mark" aria-hidden="true">${escape(mark)}</span>
      <span class="cat-picker-body">
        <span class="cat-picker-label">${escape(placeholder)}</span>
        ${hintHtml}
      </span>
      <span class="cat-picker-chevron" aria-hidden="true">▾</span>
    </button>
  `;
}

/**
 * Wire the trigger up. `selected` is the initial category object (may be null).
 * `onChange(category)` is called after the user picks a new one.
 *
 * `title`, `searchPlaceholder`, `emptyText` are forwarded to the
 * underlying sheet so the same picker mechanism can serve non-topic
 * lists (e.g. a friend picker on the duel create page).
 */
export function attachCategoryPicker(
  triggerId,
  {
    categories,
    selected,
    onChange,
    title,
    searchPlaceholder,
    emptyText,
  } = {},
) {
  const btn = document.getElementById(triggerId);
  if (!btn) return { setSelected: () => {}, getSelected: () => null };

  let current = selected || null;
  const label = btn.querySelector('.cat-picker-label');

  const paint = () => {
    if (current) {
      label.textContent = current.name;
      btn.classList.add('has-value');
    } else {
      label.textContent = btn.dataset.placeholder || 'Choose category';
      btn.classList.remove('has-value');
    }
  };
  paint();

  btn.addEventListener('click', () => {
    if (_openSheet) return; // ignore taps while a sheet is up
    openCategorySheet({
      categories,
      selectedId: current?.id || null,
      title,
      searchPlaceholder,
      emptyText,
      onSelect: (cat) => {
        current = cat;
        paint();
        if (typeof onChange === 'function') onChange(cat);
      },
    });
  });

  return {
    setSelected(cat) {
      current = cat || null;
      paint();
    },
    getSelected() {
      return current;
    },
  };
}

/**
 * Programmatic sheet open — exposed so callers can re-open without a
 * trigger button (e.g. from a "change" link). Most pages should use
 * `attachCategoryPicker` instead.
 */
export function openCategorySheet({
  categories,
  selectedId = null,
  onSelect,
  title = 'Category',
  searchPlaceholder = 'Search categories…',
  emptyText = 'No categories match.',
} = {}) {
  if (_openSheet) return; // already open

  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';
  backdrop.innerHTML = `
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Choose ${escape(title)}">
      <div class="sheet-drag" aria-hidden="true"></div>
      <div class="sheet-header">
        <div class="sheet-title">${escape(title)}</div>
        <button type="button" class="sheet-close" aria-label="Close">✕</button>
      </div>
      <div class="sheet-search-wrap">
        <span class="sheet-search-icon" aria-hidden="true">⌕</span>
        <input type="search" class="sheet-search" placeholder="${escape(searchPlaceholder)}" autocomplete="off" enterkeyhint="search" />
      </div>
      <div class="sheet-list" id="sheet-list" role="listbox" aria-label="${escape(title)}"></div>
      <div class="sheet-empty" hidden>${escape(emptyText)}</div>
    </div>
  `;
  document.body.appendChild(backdrop);
  // Force reflow so the transition kicks in on next tick.
  // eslint-disable-next-line no-unused-expressions
  backdrop.offsetHeight;
  backdrop.classList.add('open');

  const list = backdrop.querySelector('#sheet-list');
  const empty = backdrop.querySelector('.sheet-empty');
  const search = backdrop.querySelector('.sheet-search');

  const rowHtml = (c) => `
    <button type="button"
            class="sheet-item ${c.id === selectedId ? 'selected' : ''}"
            data-id="${escape(c.id)}"
            role="option"
            aria-selected="${c.id === selectedId ? 'true' : 'false'}">
      <span class="sheet-item-dot" aria-hidden="true"></span>
      <span class="sheet-item-name">${escape(c.name)}</span>
      <span class="sheet-item-check" aria-hidden="true">${c.id === selectedId ? '✓' : ''}</span>
    </button>
  `;

  const paintList = (q) => {
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? categories.filter((c) => (c.name || '').toLowerCase().includes(needle))
      : categories;
    if (!filtered.length) {
      list.innerHTML = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    list.innerHTML = filtered.map(rowHtml).join('');
  };
  paintList('');

  // If a category is already selected, scroll it into view once mounted.
  requestAnimationFrame(() => {
    const active = list.querySelector('.sheet-item.selected');
    if (active) active.scrollIntoView({ block: 'nearest' });
    // Only autofocus search on desktop — pulling the keyboard up on
    // mobile is disruptive and covers the list.
    if (window.matchMedia('(min-width: 720px)').matches) search.focus();
  });

  const close = () => {
    if (_openSheet !== backdrop) return;
    _openSheet = null;
    document.removeEventListener('keydown', onKey);
    backdrop.classList.remove('open');
    setTimeout(() => {
      backdrop.remove();
    }, 180);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);

  backdrop.addEventListener('click', (e) => {
    // Backdrop tap outside the sheet closes.
    if (e.target === backdrop) close();
  });
  backdrop.querySelector('.sheet-close').addEventListener('click', close);

  search.addEventListener('input', (e) => paintList(e.target.value));

  list.addEventListener('click', (e) => {
    const row = e.target.closest('.sheet-item');
    if (!row) return;
    const cat = categories.find((c) => c.id === row.dataset.id);
    if (!cat) return;
    if (typeof onSelect === 'function') onSelect(cat);
    close();
  });

  _openSheet = backdrop;
}
